import {
  inspectionMetadataPaths,
  VariableNumericDispatch,
  variableNumericDispatches,
  VariableTextDispatch,
} from 'ai-constants'
import {
  Inspection,
  InspectionProduct,
  InspectionVariable,
  TemplateConditionAction,
  TemplateVariable,
} from 'bff'
import get from 'lodash.get'
import {
  meetConditionTerms,
  numericalOperations,
} from '../conditions/calculate-conditions'
import { evaluateMathExpr } from '../utils/evaluate-math-expr'
import { listToMap } from '../utils/list-to-map'

type InspectionMetadataPaths = (typeof inspectionMetadataPaths)[number]

interface LocalInspection
  extends Pick<Inspection, 'consumer' | InspectionMetadataPaths> {
  template: Omit<
    Inspection['template'],
    | 'sections'
    | 'fallbackSections'
    | 'inspectionType'
    | 'createdAt'
    | 'updatedAt'
    | 'publishedAt'
    | '_id'
  >
}

export const populateInspectionAnswers = ({
  inspection,
  products,
}: {
  inspection: LocalInspection
  products: InspectionProduct[]
}) => {
  return inspection.template.inputs.map((templateInput) => {
    switch (templateInput.behaviour) {
      case 'static': {
        switch (templateInput.target) {
          case 'identity':
            return {
              ...templateInput,
              value: get(inspection.consumer, templateInput.path || ''),
            }

          case 'product':
            const productData = products.find((product) => {
              const containsTargetKey = Object.keys(
                product.data || {},
              ).includes(templateInput.path || '')

              return containsTargetKey
            })

            return {
              ...templateInput,
              value: get(productData?.data || {}, templateInput.path || ''),
            }

          default:
            return templateInput
        }
      }

      case 'dynamic':
        return templateInput
    }
  })
}

const timestamp2years = (timestampMS: number) => {
  const MILLISECONDS_PER_YEAR = 365.25 * 24 * 60 * 60 * 1000
  const years = Math.trunc(timestampMS / MILLISECONDS_PER_YEAR)

  return years
}

const mathTemplateRegex = /{{\s*([^{}]+)\s*}}/g

const isValidDate = (value: Date): boolean => !Number.isNaN(value.getTime())

const isIsoDate = (value: string): boolean => {
  if (!/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z/.test(value)) return false

  const date = new Date(value)

  if (!isValidDate(date)) return false

  return date.toISOString() === value
}

const resolveMathTemplateValue = ({
  originalValue,
}: {
  originalValue: unknown
}) => {
  if (
    originalValue === undefined ||
    originalValue === null ||
    originalValue === ''
  ) {
    return {
      castedValue: 0,
      serializedValue: '0',
    }
  }

  if (originalValue instanceof Date && isValidDate(originalValue)) {
    return {
      castedValue: originalValue,
      serializedValue: originalValue.getTime().toString(),
    }
  }

  if (typeof originalValue === 'string' && isIsoDate(originalValue)) {
    const castedValue = new Date(originalValue)

    return {
      castedValue,
      serializedValue: castedValue.getTime().toString(),
    }
  }

  if (typeof originalValue === 'boolean') {
    return {
      castedValue: originalValue,
      serializedValue: Number(originalValue).toString(),
    }
  }

  return {
    castedValue: originalValue,
    serializedValue: String(originalValue),
  }
}

const compileMathTemplate = ({
  context,
  onCastedValue,
  template,
}: {
  context: {
    inputs: Record<string, unknown>
    inspection: LocalInspection
    variables: Record<string, unknown>
  }
  onCastedValue?: (input: {
    castedValue: unknown
    originalValue: unknown
  }) => void
  template: string
}) =>
  template.replace(mathTemplateRegex, (_, match) => {
    const originalValue = get(context, match)
    const { castedValue, serializedValue } = resolveMathTemplateValue({
      originalValue,
    })

    onCastedValue?.({
      originalValue,
      castedValue,
    })

    return serializedValue
  })

const isVariableAction = (
  action: TemplateConditionAction,
): action is Required<
  Omit<TemplateConditionAction, 'dispatch'> & {
    dispatch: VariableNumericDispatch | VariableTextDispatch
  }
> =>
  variableNumericDispatches.some((dispatch) => action.dispatch === dispatch) &&
  !!action.value

export const computeInspectionDynamicProps = ({
  inspection,
  products,
}: {
  inspection: LocalInspection
  products: InspectionProduct[]
}) => {
  const inputs = populateInspectionAnswers({
    inspection,
    products,
  })

  const immediateVariables: TemplateVariable[] = inspection.template.variables
  const deferredFunctions = inspection.template.functions || []

  const variables = (immediateVariables || []).map((variable) => {
    const conditions = inspection.template.conditions.filter((condition) => {
      return condition.actions.some(
        (a) =>
          a.target.type === 'variable' &&
          a.target.value.includes(variable.name),
      )
    })

    return {
      ...variable,
      value: variable.initialValue,
      conditions,
    }
  })

  for (const variable of variables) {
    for (const condition of variable.conditions) {
      const termsMeet = meetConditionTerms({
        inputs,
        link: condition.link,
        terms: condition.terms,
        variables,
        functions: deferredFunctions,
      })

      if (termsMeet) {
        for (const action of condition.actions) {
          if (isVariableAction(action)) {
            const modifier = (() => {
              switch (action.value.type) {
                case 'constant':
                  return action.value.value

                case 'variable':
                  return variables.find((v) => v.name === action.value.value)
                    ?.value

                case 'input':
                  return inputs.find(
                    (field) => field.identifier === action.value.value,
                  )?.value
              }
            })()

            const value = (() => {
              switch (action.dispatch) {
                case 'replace':
                  return modifier

                default:
                  const fn = numericalOperations[action.dispatch]

                  return fn(Number(variable.value), modifier)
              }
            })()

            variable.value = value
          }
        }
      }
    }
  }

  const deferredVariablesComputed = (deferredFunctions || []).map((fn) => {
    const variableValues: unknown[] = []
    const formula = compileMathTemplate({
      template: fn.expression,
      context: {
        inputs: listToMap(inputs, 'identifier', 'value'),
        variables: listToMap(variables, 'name', 'value'),
        inspection,
      },
      onCastedValue: ({ castedValue }) => {
        variableValues.push(castedValue)
      },
    })

    const computedExpressionValue = evaluateMathExpr(formula)

    const needsToBeConvertedToYears =
      variableValues.length > 0 &&
      variableValues.every(
        (value) => typeof value === 'object' && value instanceof Date,
      )

    if (needsToBeConvertedToYears) {
      return {
        name: fn.name,
        value: timestamp2years(computedExpressionValue),
      }
    }

    return {
      name: fn.name,
      value: computedExpressionValue,
    }
  })

  return {
    fns: deferredVariablesComputed,
    variables: variables.map((variable) => ({
      name: variable.name,
      value: variable.value,
    })),
  }
}
